import { pointValue } from './tools/contracts';
import { privacyOn } from './privacy';

// Gross P&L in dollars. Uses the broker's figure when the trade was imported with one;
// otherwise the price move times the contract's dollars per point (1 for stocks).
export function grossPnl(t) {
  if (typeof t.pnl === 'number') return t.pnl;
  const direction = t.side === 'Short' ? -1 : 1;
  return (t.exitPrice - t.entryPrice) * (t.size || 1) * direction * (pointValue(t.symbol) ?? 1);
}

export function netPnl(t) {
  return grossPnl(t) - (t.fees || 0);
}

const dollars = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
// Hidden amounts (the journal's Privacy display) keep their sign so green and red still read.
const HIDDEN = '$•••';

export const usd = { format: (value) => (privacyOn() ? HIDDEN : dollars.format(value)) };

export function formatMoney(value) {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${privacyOn() ? HIDDEN : dollars.format(Math.abs(value))}`;
}

// Short form for tight spaces like calendar cells: +$347, -$1.2k
export function formatMoneyCompact(value) {
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  if (privacyOn()) return `${sign}${HIDDEN}`;
  const abs = Math.abs(value);
  const amount = abs >= 1000 ? `${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1)}k` : Math.round(abs).toString();
  return `${sign}$${amount}`;
}

// How long a trade was open, in milliseconds, or null without an exit time.
export function holdMs(t) {
  if (!t.exitDate) return null;
  const ms = new Date(t.exitDate) - new Date(t.date);
  return ms >= 0 ? ms : null;
}

// A hold time for display: 45s, 4m 12s, 1h 05m, 2d 3h.
export function formatDuration(ms) {
  if (ms === null || ms === undefined) return '';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, '0')}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

// The calendar day a date falls on in the viewer's time zone, as YYYY-MM-DD.
export function dayKey(date) {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Futures trading days run from the 6 PM New York reopen to the 5 PM close the next
// day, so a trade at or after 6 PM New York time belongs to the next day's session
// (Sunday evening counts as Monday). Used for everything grouped by day of trading:
// the calendar, daily loss limits, consistency and Insights. Notes stay on the
// trader's own calendar day.
const NEW_YORK = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
});

export function tradingDayKey(date) {
  const parts = Object.fromEntries(NEW_YORK.formatToParts(new Date(date)).map((p) => [p.type, p.value]));
  const y = Number(parts.year);
  const m = Number(parts.month);
  const d = Number(parts.day) + (Number(parts.hour) >= 18 ? 1 : 0);
  return new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
}

// The trading day happening now.
export function currentTradingDay() {
  return tradingDayKey(new Date());
}
